import {Component, computed, effect, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {RoutesAddonsEnums} from 'src/app/enums/RoutesEnums';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {SearchService} from '../../../../services/search/search.service';
import {Breadcrumb} from '../../../../types/Breadcrumb';
import {SearchWidgetDataItem} from '../../../../types/SearchWidgetDataItem';
import {RelatedService} from '../../services/related.service';
import {RelatedLink} from '../../types/RelatedLink';
import {RelatedCategoryConfigService} from '../../services/related-category-config.service';
import {CategoryConfig} from '../../types/CategoryConfig';
import {RelatedLinksService} from '../../services/related-links.service';
import {RelatedNavigationService} from '../../services/related-navigation.service';

@Component({
  selector: 'app-related-category',
  templateUrl: './related-category.component.html',
})
export class RelatedCategoryComponent implements OnInit, OnDestroy {
  public categoryType = signal<RoutesAddonsEnums>(null);
  public categoryConfig = signal<CategoryConfig>(null);
  public h1Title = computed<string>(() => this.categoryConfig()?.h1Title || '');
  public introText = computed<string>(() => this.categoryConfig()?.introText || '');
  public pageType: DLPageTypeEnum = DLPageTypeEnum.OTHER_PAGE;
  public breadcrumbs = signal<Breadcrumb[]>([]);
  protected widgetDataItems = signal<SearchWidgetDataItem[]>([]);

  private searchService = inject(SearchService);
  private configService = inject(RelatedCategoryConfigService);
  private linksService = inject(RelatedLinksService);
  private navigationService = inject(RelatedNavigationService);
  private subscriptions: Subscription[] = [];

  public categoryItems = computed<RelatedLink[]>(() => {
    return this.linksService.generateCategoryLinks(this.categoryType(), this.widgetDataItems());
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private relatedService: RelatedService,
  ) {
    effect(
      () => {
        const config = this.categoryConfig();
        if (this.categoryType() && config) {
          this.relatedService.updateMetaTags(this.categoryType(), config.typeName, null, config.addon);
          this.relatedService.trackPageView(this.categoryType(), config.typeName, null);
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.categoryType()) {
          this.linksService.getWidgetDataForCategory(this.categoryType()).subscribe({
            next: (data) => {
              if (!data || data.length === 0) {
                return;
              }
              this.widgetDataItems.set(data);
            },
          });
        }
      },
      {allowSignalWrites: true},
    );
  }

  ngOnInit(): void {
    this.searchService.softResetState();
    this.onRouteUpdate();
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onRouteUpdate();
        }
      }),
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private onRouteUpdate(): void {
    const categoryType = this.navigationService.getCategoryTypeFromRoute(this.route);
    this.categoryType.set(categoryType);

    const config = this.configService.getCategoryConfig(categoryType);
    this.categoryConfig.set(config);

    const breadcrumbs = this.navigationService.buildCategoryBreadcrumbs(categoryType);
    this.breadcrumbs.set(breadcrumbs);
  }
}
